import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster } from "sonner";
import MarketingNav from "@/components/MarketingNav";
import AnnouncementBar from "@/components/AnnouncementBar";
import SiteFooter from "@/components/SiteFooter";
import AudienceDialog from "@/components/AudienceDialog";
import { DriftingLetters, FloatingNotes, Sparkles } from "@/components/BackgroundFx";
import JmlLink from "@/components/JmlLink";
import { ShowRow } from "@/pages/Performances";
import { PHOTOS } from "@/lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REFLECTIONS = [
  {
    quote:
      "Wow! Just wow! Your take on the songs and your playing were extremely moving! It's been a while since I've seen someone speak through an instrument with the fluency and emotion as you did.",
    who: "Eduardo S.",
    photo: PHOTOS.warmRoom,
  },
  {
    quote:
      "Having the opportunity to hear him during the piano soundbath for over an hour without a sheet of music was so special. His music is a gift from Hashem.",
    who: "Bina B.",
    photo: PHOTOS.soundbathHero,
  },
  {
    quote:
      "The music was beautiful and unique. It was a very spiritual and uplifting experience.",
    who: "Miriam Y.",
    photo: PHOTOS.soundbathRoom,
  },
];

function Aurora() {
  return (
    <>
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 0 }}
        animate={{
          background: [
            "radial-gradient(60% 50% at 20% 20%, rgba(167,116,255,0.55) 0%, transparent 60%), radial-gradient(50% 40% at 80% 30%, rgba(255,160,90,0.40) 0%, transparent 60%), radial-gradient(80% 60% at 50% 100%, rgba(80,40,180,0.55) 0%, transparent 70%)",
            "radial-gradient(55% 45% at 75% 15%, rgba(120,210,255,0.45) 0%, transparent 60%), radial-gradient(60% 50% at 15% 70%, rgba(255,120,180,0.40) 0%, transparent 60%), radial-gradient(70% 55% at 60% 100%, rgba(60,30,160,0.55) 0%, transparent 70%)",
            "radial-gradient(50% 60% at 30% 80%, rgba(120,255,200,0.40) 0%, transparent 60%), radial-gradient(60% 40% at 80% 60%, rgba(255,200,120,0.40) 0%, transparent 60%), radial-gradient(80% 60% at 40% 0%, rgba(60,30,160,0.55) 0%, transparent 70%)",
            "radial-gradient(60% 50% at 20% 20%, rgba(167,116,255,0.55) 0%, transparent 60%), radial-gradient(50% 40% at 80% 30%, rgba(255,160,90,0.40) 0%, transparent 60%), radial-gradient(80% 60% at 50% 100%, rgba(80,40,180,0.55) 0%, transparent 70%)",
          ],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          zIndex: 0,
          backgroundImage:
            'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22240%22 height=%22240%22><filter id=%22n%22><feTurbulence baseFrequency=%220.85%22 numOctaves=%222%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
        }}
      />
    </>
  );
}

export default function Home() {
  const [shows, setShows] = useState(null);
  const [artistUrl, setArtistUrl] = useState("");
  const [past, setPast] = useState([]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/shows`, { params: { limit: 6 } })
      .then(({ data }) => {
        if (!alive) return;
        setShows(data.shows || []);
        setArtistUrl(data.artist_url || "");
        // Nothing coming up? Show where the piano has been instead.
        if ((data.shows || []).length === 0 && (data.past_count || 0) > 0) {
          return axios.get(`${API}/shows`, { params: { past: 1, limit: 3 } }).then((r) => {
            if (alive) setPast(r.data.shows || []);
          });
        }
        return undefined;
      })
      .catch(() => {
        if (alive) setShows([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      className="relative min-h-screen w-full"
      style={{
        backgroundColor: "#080419",
        color: "#F5EFE3",
        fontFamily: "'Courier Prime', monospace",
        overflowX: "hidden",
      }}
      data-testid="home-page"
    >
      <Toaster position="bottom-right" richColors />

      <Aurora />
      <DriftingLetters count={26} />
      <FloatingNotes count={7} />
      <Sparkles count={50} />

      <div className="relative" style={{ zIndex: 1 }}>
        <AnnouncementBar />
        <MarketingNav active="/" />

        {/* Hero */}
        <section className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-14 sm:pt-24 pb-12" data-testid="home-hero">
          <div className="flex flex-col lg:grid lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:items-center gap-10">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="text-[11px] uppercase tracking-[0.36em] mb-5"
                style={{ color: "#F2D589" }}
                data-testid="hero-eyebrow"
              >
                pianist &middot; improviser &middot; educator
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="leading-[0.95] tracking-tight lg:whitespace-nowrap"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: "clamp(40px, 4.4vw, 58px)",
                  textWrap: "balance",
                }}
                data-testid="hero-title"
              >
                Jazz Meets{" "}
                <em style={{ fontStyle: "italic", color: "#F2D589", textShadow: "0 0 28px rgba(242,213,137,0.45)" }}>
                  Jewish
                </em>
                <span style={{ color: "#FCA07A" }}>.</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.28 }}
                className="mt-5 max-w-[38rem]"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  fontSize: "clamp(20px, 1.9vw, 27px)",
                  lineHeight: 1.25,
                  color: "rgba(245,239,227,0.92)",
                  textWrap: "balance",
                }}
                data-testid="hero-subhead"
              >
                Unforgettable experiences at the intersection of kabbalah and
                musical creativity.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="mt-6 max-w-[42rem] text-base sm:text-lg leading-relaxed"
                style={{ color: "rgba(245,239,227,0.78)" }}
              >
                Performances rooted in jazz language, nigunim, and
                the strange beautiful dissonances of Eastern Europe, built for
                Jewish communities, and shaped live by the room they&apos;re
                played in.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Link
                  to="/community-events"
                  data-testid="hero-book-cta"
                  className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em]"
                  style={{
                    backgroundColor: "#F2D589",
                    color: "#080419",
                    boxShadow: "0 0 40px rgba(242,213,137,0.35)",
                  }}
                >
                  Book A Concert
                </Link>
                <Link
                  to="/music"
                  data-testid="hero-listen-cta"
                  className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-white/25 text-white/90 hover:bg-white/10 transition-colors"
                >
                  Listen
                </Link>
                <AudienceDialog
                  testIdPrefix="hero-subscribe-dialog"
                  trigger={
                    <button
                      type="button"
                      data-testid="hero-subscribe-button"
                      className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-[#F2D589]/40 transition-colors hover:bg-[#F2D589]/10"
                      style={{ color: "#F2D589" }}
                    >
                      Subscribe
                    </button>
                  }
                />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="relative justify-self-center"
            >
              <div
                className="relative rounded-2xl overflow-hidden"
                style={{
                  width: "min(400px, 84vw)",
                  aspectRatio: "3 / 4",
                  boxShadow:
                    "0 60px 120px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(242,213,137,0.18) inset",
                }}
              >
                <img
                  src={PHOTOS.homeHero}
                  alt="Dan Berry at the piano"
                  className="w-full h-full object-cover"
                  data-testid="hero-photo"
                  loading="eager"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(8,4,25,0) 50%, rgba(8,4,25,0.55) 100%)",
                  }}
                />
              </div>
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-3xl"
                style={{
                  background:
                    "radial-gradient(60% 60% at 50% 50%, rgba(242,213,137,0.18) 0%, transparent 70%)",
                  zIndex: -1,
                  filter: "blur(8px)",
                }}
              />
            </motion.div>
          </div>
        </section>

        {/* About */}
        <section id="about" className="max-w-[1200px] mx-auto px-6 sm:px-10 py-20 sm:py-28" data-testid="about-section">
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            About
          </p>
          <h2
            className="text-4xl sm:text-6xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            data-testid="about-heading"
          >
            Hi, I&apos;m <em style={{ color: "#F2D589" }}>Dan</em>.
          </h2>

          <div className="mt-10 grid md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)] gap-8 md:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl overflow-hidden border"
              style={{ borderColor: "rgba(255,255,255,0.12)", aspectRatio: "4 / 5" }}
              data-testid="about-photo"
            >
              <img
                src={PHOTOS.berryFamilyBW}
                alt="Dan Berry"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>

            <div className="space-y-4 text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.82)" }}>
              <p>
                I&apos;m a pianist and composer playing where Jewish music meets
                the jazz and classical traditions I grew up on. High Holiday
                melodies, klezmer-soaked folk songs, and niggunim that take the
                listener somewhere exotic yet strangely familiar.
              </p>
              <p>
                This journey started at Princeton, where I graduated with a
                degree in composition and a whole new appreciation for Jewish
                life. Years of performing in both jazz rooms and Jewish ones
                turned into an experiment: what happens when those two worlds
                actually talk to each other? That conversation led to the ideas for
                both <em>Jazz Meets Jewish</em> and{" "}
                <JmlLink
                  className="underline decoration-[#F2D589]/40 underline-offset-4 transition-colors hover:text-[#F2D589]"
                  style={{ color: "#F2D589" }}
                  testId="about-alephonics-link"
                  arrow={false}
                >
                  Alephonics
                </JmlLink>{" "}
                — two musical experiences that didn&apos;t exist before.
              </p>
              <p>
                Every performance is different. Not just improvised, but{" "}
                <em>responsive</em> to the room, to the people in it, to the
                echoes of where we all come from. Sometimes it&apos;s a wedding.
                Sometimes it&apos;s a name turned into a melody. Sometimes
                it&apos;s just a piano and a bunch of strangers who become a
                little less strange together.
              </p>
              <p>
                Jewish music today isn&apos;t a museum, it&apos;s a living,
                breathing creative practice. I&apos;m here to play it like it is.
              </p>
            </div>
          </div>
        </section>

        {/* Reflections */}
        <section id="reflections" className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16" data-testid="reflections-section">
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            When the music lands
          </p>
          <h2
            className="text-3xl sm:text-5xl leading-tight mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Reflections from the room.
          </h2>
          <ul className="grid md:grid-cols-3 gap-5">
            {REFLECTIONS.map((r, idx) => (
              <motion.li
                key={r.who}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className="rounded-2xl border overflow-hidden backdrop-blur-md flex flex-col"
                style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.4)" }}
              >
                <div className="w-full overflow-hidden" style={{ aspectRatio: "16 / 11" }}>
                  <img src={r.photo} alt="" className="w-full h-full object-cover opacity-90" loading="lazy" />
                </div>
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <p
                    className="text-base sm:text-lg italic leading-relaxed"
                    style={{ color: "rgba(245,239,227,0.88)", fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    &ldquo;{r.quote}&rdquo;
                  </p>
                  <p className="text-xs mt-auto" style={{ color: "rgba(245,239,227,0.55)" }}>
                    {r.who}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* Performances */}
        <section id="performances" className="max-w-[1200px] mx-auto px-6 sm:px-10 py-16" data-testid="performances-section">
          <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
            Upcoming
          </p>
          <h2
            className="text-3xl sm:text-5xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Live performances
          </h2>

          {shows === null ? (
            <p className="mt-6 text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="home-shows-loading">
              Checking the calendar…
            </p>
          ) : shows.length === 0 ? (
            <div
              className="mt-6 rounded-2xl border px-6 py-10 sm:px-10 sm:py-14 text-center backdrop-blur-md"
              style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.4)" }}
              data-testid="performances-empty"
            >
              <p
                className="text-2xl sm:text-3xl"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontStyle: "italic" }}
              >
                No dates announced yet.
              </p>
              <p className="mt-3 text-sm" style={{ color: "rgba(245,239,227,0.65)" }}>
                {artistUrl ? (
                  <>
                    <a
                      href={`${artistUrl}${artistUrl.includes("?") ? "&" : "?"}trigger=track`}
                      target="_blank"
                      rel="noreferrer"
                      className="underline underline-offset-4 hover:text-[#F2D589]"
                      data-testid="home-shows-follow"
                    >
                      Follow on Bandsintown
                    </a>{" "}
                    to be told first.{" "}
                  </>
                ) : null}
                For bookings — shuls, Chabad houses, federations, communities of
                any size —{" "}
                <Link to="/community-events" className="underline underline-offset-4 hover:text-[#F2D589]">
                  start a conversation
                </Link>
                .
              </p>
              {past.length > 0 ? (
                <>
                  <p
                    className="mt-10 mb-5 text-[10px] uppercase tracking-[0.32em]"
                    style={{ color: "#FCA07A" }}
                  >
                    Where the piano has been
                  </p>
                  <ul className="grid gap-4 text-left opacity-80" data-testid="home-past-shows-list">
                    {past.slice(0, 3).map((s, i) => (
                      <ShowRow key={s.id} show={s} index={i} testIdPrefix="home-past-show" isPast />
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link
                      to="/performances"
                      data-testid="home-past-shows-all"
                      className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-[#F2D589]/40 transition-colors hover:bg-[#F2D589]/10"
                      style={{ color: "#F2D589" }}
                    >
                      See past shows &rarr;
                    </Link>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <>
              <ul className="mt-8 grid gap-4" data-testid="home-shows-list">
                {shows.slice(0, 3).map((s, i) => (
                  <ShowRow key={s.id} show={s} index={i} testIdPrefix="home-show" />
                ))}
              </ul>
              <div className="mt-8">
                <Link
                  to="/performances"
                  data-testid="home-shows-all"
                  className="inline-flex items-center justify-center rounded-full px-6 h-12 text-sm uppercase tracking-[0.22em] border border-[#F2D589]/40 transition-colors hover:bg-[#F2D589]/10"
                  style={{ color: "#F2D589" }}
                >
                  All upcoming dates &rarr;
                </Link>
              </div>
            </>
          )}
        </section>

        <SiteFooter />
      </div>
    </div>
  );
}
